/* eslint-disable local/ensure-wac-token-usage */
import { MouseEventHandler } from "react";

import { css } from "@compiled/react";

import { DataEventProps } from "@/libraries/wpl/foundations/src/index";

type ButtonSize = "large" | "small";

export type ButtonColor =
    | "darkblue"
    | "blueprimary700"
    | "yellow"
    | "green"
    | "lightblue"
    | "white"
    | "whiteblue"
    | "gray"
    | "disabled";
export type ButtonType = "primary" | "secondary";
type InputTypes = "button" | "submit" | "reset";

interface ButtonStyle {
    textColor: string;
    buttonBackgroundColor: string;
    buttonHoverColor: string;
    buttonFocusColor: string;
    border: string;
}

const buttonStyles: Record<
    ButtonType,
    Partial<Record<ButtonColor, ButtonStyle>>
> = {
    primary: {
        // default styles
        darkblue: {
            textColor: "#ffffff",
            buttonBackgroundColor: "#0052cc",
            buttonHoverColor: "#0047B3",
            buttonFocusColor: "#004099",
            border: "none"
        },
        blueprimary700: {
            textColor: "#ffffff",
            buttonBackgroundColor: "#1868DB",
            buttonHoverColor: "#0047B3",
            buttonFocusColor: "#004099",
            border: "none"
        },
        yellow: {
            textColor: "#172b4d",
            buttonBackgroundColor: "#ffab00",
            buttonHoverColor: "#ff991f",
            buttonFocusColor: "#ff8b00",
            border: "none"
        },
        green: {
            textColor: "#172b4d",
            buttonBackgroundColor: "#79f2c0",
            buttonHoverColor: "#57d9a3",
            buttonFocusColor: "#36b37e",
            border: "none"
        },
        lightblue: {
            textColor: "#0052cc",
            buttonBackgroundColor: "#deebff",
            buttonHoverColor: "#B3D4FF",
            buttonFocusColor: "#4c9aff",
            border: "none"
        },
        gray: {
            textColor: "#172b4d",
            buttonBackgroundColor: "#ebecf0",
            buttonHoverColor: "#DFE1E6",
            buttonFocusColor: "#c1c7d0",
            border: "none"
        },
        disabled: {
            textColor: "#97a0af",
            buttonBackgroundColor: "#ebecf0",
            buttonHoverColor: "",
            buttonFocusColor: "",
            border: "none"
        }
    },
    secondary: {
        darkblue: {
            textColor: "#0052cc",
            buttonBackgroundColor: "transparent",
            buttonHoverColor: "#ebecf0",
            buttonFocusColor: "#deebff",
            border: `0.063rem solid #0052cc`
        },
        blueprimary700: {
            textColor: "#1868DB",
            buttonBackgroundColor: "transparent",
            buttonHoverColor: "#ebecf0",
            buttonFocusColor: "#deebff",
            border: `0.063rem solid #1868DB`
        },
        yellow: {
            textColor: "#172b4d",
            buttonBackgroundColor: "transparent",
            buttonHoverColor: "#f4f5f7",
            buttonFocusColor: "#FFF0B3",
            border: `0.063rem solid #ffab00`
        },
        green: {
            textColor: "#172b4d",
            buttonBackgroundColor: "transparent",
            buttonHoverColor: "#f4f5f7",
            buttonFocusColor: "#e3fcef",
            border: `0.063rem solid #79f2c0`
        },
        white: {
            textColor: "#ffffff",
            buttonBackgroundColor: "transparent",
            buttonHoverColor: "rgba(255, 255, 255, 0.10)",
            buttonFocusColor: "rgba(255, 255, 255, 0.20)",
            border: `0.063rem solid #ffffff`
        },
        disabled: {
            textColor: "#97a0af",
            buttonBackgroundColor: "#ebecf0",
            buttonHoverColor: "",
            buttonFocusColor: "",
            border: "none"
        },
        whiteblue: {
            textColor: "#ffffff",
            buttonBackgroundColor: "transparent",
            buttonHoverColor: "rgba(255, 255, 255, 0.10)",
            buttonFocusColor: "rgba(255, 255, 255, 0.20)",
            border: `0.063rem solid #0052cc`
        }
    }
};

const getButtonSize = (size: string | undefined): ButtonSize => {
    const buttonSize = size?.toLowerCase();
    if (buttonSize && buttonSize === "small") {
        return "small";
    }
    return "large";
};

const getButtonType = (type: string | undefined): ButtonType => {
    const buttonType = type?.toLowerCase();
    if (buttonType === "secondary") {
        return "secondary";
    }
    return "primary";
};

const isButtonColor = (color: string): color is ButtonColor => {
    return (
        color === "darkblue" ||
        color === "blueprimary700" ||
        color === "yellow" ||
        color === "green" ||
        color === "lightblue" ||
        color === "white" ||
        color === "gray" ||
        color === "disabled"
    );
};

const getButtonColor = (
    color: string | undefined,
    disabled: boolean | undefined
): ButtonColor => {
    if (disabled) {
        return "disabled";
    }

    const buttonColor = color?.toLowerCase();

    if (buttonColor && isButtonColor(buttonColor)) {
        return buttonColor;
    }

    return "darkblue";
};

const getButtonStyles = (type: ButtonType, color: ButtonColor): ButtonStyle => {
    const styles = buttonStyles[type][color];

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return styles ? styles : buttonStyles.primary.darkblue!;
};

/**
 * @deprecated - Please use @/libraries/design/components/button instead
 */
export interface ButtonProps {
    text: string;
    type?: ButtonType;
    color?: ButtonColor;
    size?: ButtonSize;
    onClick?: MouseEventHandler;
    url?: string;
    externalLink?: boolean;
    inputType?: InputTypes;
    disabled?: boolean;
    className?: string;
    dataEventProps?: DataEventProps;
}

/**
 * @deprecated - Please use @/libraries/design/components/button instead
 */
export const Button = ({
    text,
    type,
    color = "darkblue",
    size,
    onClick,
    url,
    externalLink,
    inputType,
    disabled,
    className,
    dataEventProps
}: ButtonProps) => {
    const buttonType = getButtonType(type);
    const buttonColor = getButtonColor(color, disabled);
    const {
        textColor,
        buttonBackgroundColor,
        buttonHoverColor,
        buttonFocusColor,
        border
    } = getButtonStyles(buttonType, buttonColor);
    const buttonSize: string = getButtonSize(size);
    const fontSize = buttonSize === "large" ? "1rem" : "0.875rem";
    const height = buttonSize === "large" ? "2.5rem" : "2rem";
    const cursor = disabled ? "not-allowed" : "pointer";

    const styles = css({
        "alignItems": "center",
        "justifyContent": "center",
        "background": buttonBackgroundColor,
        "borderRadius": "0.25rem",
        "boxSizing": "border-box",
        "border": border,
        "color": textColor,
        "display": "inline-flex",
        "fontWeight": 500, // computed fontWeight of 500 equivalent to fontWeight of 600 on Figma
        "fontFamily": "var(--bxp-charlie-text-font-family)",
        "lineHeight": "1.25rem",
        "padding": "0 1rem",
        "textAlign": "center",
        "textDecoration": "none",
        ":hover, :focus": {
            background: buttonHoverColor
        },
        ":active": {
            background: buttonFocusColor
        },
        "fontSize": fontSize,
        "height": height,
        "cursor": cursor
    });

    const handleOnClick = (e: React.MouseEvent) => {
        if (disabled) {
            e.preventDefault();
        } else {
            onClick?.(e);
        }
    };

    if ((onClick || inputType) && !url) {
        return (
            <button
                className={className}
                css={styles}
                onClick={handleOnClick}
                disabled={disabled}
                type={inputType}
                {...dataEventProps}
            >
                {text}
            </button>
        );
    }

    return (
        <a
            className={className}
            css={styles}
            onClick={handleOnClick}
            href={url}
            target={externalLink ? "_blank" : undefined}
            rel={externalLink ? "noreferrer" : undefined}
            role="button"
            {...dataEventProps}
        >
            {text}
        </a>
    );
};
